import { useCallback, useMemo } from "react";

const PERMISSIONS_KEY = "sessionPermissions";

/**
 * Read the permissions array stored by login.
 * Returns { permissions, hasPermission, hasAny, canSeeAll, isAdmin }
 */
const usePermissions = () => {
  const permissions = useMemo(() => {
    try {
      const raw = localStorage.getItem(PERMISSIONS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }, []);

  const user = useMemo(() => {
    try {
      const raw = localStorage.getItem("sessionUser");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const isAdmin = useMemo(() => user?.role === "admin", [user]);

  const hasPermission = useCallback(
    (perm) => {
      if (isAdmin) return true;
      return permissions.includes(perm);
    },
    [permissions, isAdmin]
  );

  const hasAny = useCallback(
    (perms) => {
      if (isAdmin) return true;
      return perms.some((p) => permissions.includes(p));
    },
    [permissions, isAdmin]
  );

  const canSeeAll = useCallback(
    (module) => {
      if (isAdmin) return true;
      return permissions.includes(`data.${module}.all`);
    },
    [permissions, isAdmin]
  );

  return { permissions, hasPermission, hasAny, canSeeAll, isAdmin };
};

export default usePermissions;
